<script setup>
import { reactive, onMounted, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import BrandVoiceAPI from 'dashboard/api/brandVoice';
import { useAlert } from 'dashboard/composables';
import { useEscClose } from 'dashboard/composables/useEscClose';
import { useTracking } from 'dashboard/composables/useTracking';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';

import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisField from 'dashboard/components-next/LaisField.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import LaisConfirmModal from 'dashboard/components-next/LaisConfirmModal.vue';
import LaisToggle from 'dashboard/components-next/LaisToggle.vue';

const props = defineProps({
  embedded: { type: Boolean, default: false },
});

const { t } = useI18n();
const { track } = useTracking();
const isLoading = ref(true);
const isSaving = ref(false);
const isGuideSaving = ref(false);
const isGuidePublishing = ref(false);
const showRestoreModal = ref(false);
const selectedGuideVersionId = ref('');
const guideVersions = ref([]);
const defaultGuideSections = ref([]);
const defaultPersonalityAxes = ref([]);
const defaultVoiceTraits = ref([]);

const form = reactive({
  brand_voice_persona: '',
  brand_voice_tone: '',
  brand_voice_examples: '',
  brand_voice_avoid_words: '',
  brand_voice_greeting: '',
  brand_voice_signature: '',
  brand_voice_enabled_for_agent: true,
  brand_voice_personality_axes: [],
  brand_voice_voice_traits: [],
});

const guideForm = reactive({
  id: null,
  title: '',
  version: '',
  status: 'draft',
  summary: '',
  sections: [],
});

const statusLabel = computed(() => {
  if (guideForm.status === 'published') {
    return t('BRAND_VOICE.GUIDE.STATUS.PUBLISHED');
  }
  if (guideForm.status === 'archived') {
    return t('BRAND_VOICE.GUIDE.STATUS.ARCHIVED');
  }
  return t('BRAND_VOICE.GUIDE.STATUS.DRAFT');
});

const guideVersionOptions = computed(() =>
  guideVersions.value.map(version => ({
    value: String(version.id),
    label: `${version.version} · ${version.title}`,
  }))
);

function corpoParaTexto(body) {
  return (Array.isArray(body) ? body : [])
    .map(block => block?.text)
    .filter(Boolean)
    .join('\n\n');
}

const clonarLista = items => items.map(item => ({ ...item }));

const normalizarEixos = axes =>
  clonarLista(axes).map((axis, index) => ({
    left:
      axis.left || t('BRAND_VOICE.DEFAULTS.AXIS_LEFT', { number: index + 1 }),
    right:
      axis.right || t('BRAND_VOICE.DEFAULTS.AXIS_RIGHT', { number: index + 1 }),
    value: Number(axis.value ?? 50),
    note: axis.note || '',
  }));

const normalizarTracos = traits =>
  clonarLista(traits).map((trait, index) => ({
    is: trait.is || t('BRAND_VOICE.DEFAULTS.TRAIT_IS', { number: index + 1 }),
    is_not: trait.is_not || '',
  }));

function normalizarSecoes(sections) {
  return sections.map((section, index) => ({
    id: section.id || null,
    slug: section.slug || `secao-${index + 1}`,
    title:
      section.title ||
      t('BRAND_VOICE.DEFAULTS.SECTION_TITLE', { number: index + 1 }),
    position: section.position || index + 1,
    body_text: section.body_text || corpoParaTexto(section.body),
    metadata: section.metadata || {},
  }));
}

function aplicarGuia(data) {
  const current = data.current_version || {};
  guideVersions.value = data.versions || [];
  defaultGuideSections.value = data.default_sections || [];
  selectedGuideVersionId.value = current.id ? String(current.id) : '';

  guideForm.id = current.id || null;
  guideForm.title = current.title || t('BRAND_VOICE.DEFAULTS.GUIDE_TITLE');
  guideForm.version =
    current.version || t('BRAND_VOICE.DEFAULTS.GUIDE_VERSION');
  guideForm.status = current.status || 'draft';
  guideForm.summary = current.summary || '';
  guideForm.sections = normalizarSecoes(
    current.sections?.length ? current.sections : defaultGuideSections.value
  );
}

async function carregarCampos() {
  const { data } = await BrandVoiceAPI.get();
  form.brand_voice_persona = data.persona || '';
  form.brand_voice_tone = data.tone || '';
  form.brand_voice_examples = data.examples || '';
  form.brand_voice_avoid_words = data.avoid_words || '';
  form.brand_voice_greeting = data.greeting || '';
  form.brand_voice_signature = data.signature || '';
  form.brand_voice_enabled_for_agent = data.enabled_for_agent ?? true;
  defaultPersonalityAxes.value = data.default_personality_axes || [];
  defaultVoiceTraits.value = data.default_voice_traits || [];
  form.brand_voice_personality_axes = normalizarEixos(
    data.personality_axes?.length
      ? data.personality_axes
      : defaultPersonalityAxes.value
  );
  form.brand_voice_voice_traits = normalizarTracos(
    data.voice_traits?.length ? data.voice_traits : defaultVoiceTraits.value
  );
}

async function carregarGuia(versionId = selectedGuideVersionId.value) {
  const { data } = await BrandVoiceAPI.guide({ version_id: versionId });
  aplicarGuia(data);
}

const carregar = async () => {
  isLoading.value = true;
  try {
    await Promise.all([carregarCampos(), carregarGuia()]);
  } catch {
    useAlert(t('BRAND_VOICE.ALERTS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const salvar = async () => {
  isSaving.value = true;
  try {
    await BrandVoiceAPI.update(form);
    track(ACCOUNT_EVENTS.BRAND_VOICE_SAVED, {
      enabledForAgent: form.brand_voice_enabled_for_agent,
      axisCount: form.brand_voice_personality_axes.length,
      traitCount: form.brand_voice_voice_traits.length,
    });
    useAlert(t('BRAND_VOICE.ALERTS.SAVE_SUCCESS'));
  } catch {
    useAlert(t('BRAND_VOICE.ALERTS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const guiaPayload = () => ({
  id: guideForm.id,
  title: guideForm.title,
  version: guideForm.version,
  status: guideForm.status || 'draft',
  summary: guideForm.summary,
  sections: guideForm.sections.map((section, index) => ({
    id: section.id,
    slug: section.slug,
    title: section.title,
    position: index + 1,
    body_text: section.body_text,
    metadata: section.metadata || {},
  })),
});

const salvarGuia = async ({ silencioso = false } = {}) => {
  if (!guideForm.title.trim() || !guideForm.version.trim()) {
    useAlert(t('BRAND_VOICE.ALERTS.GUIDE_REQUIRED'));
    return null;
  }

  isGuideSaving.value = true;
  try {
    const { data } = await BrandVoiceAPI.updateGuide(guiaPayload());
    aplicarGuia(data);
    if (!silencioso) {
      track(ACCOUNT_EVENTS.BRAND_VOICE_GUIDE_SAVED, {
        version: guideForm.version,
        sectionCount: guideForm.sections.length,
        status: guideForm.status,
      });
      useAlert(t('BRAND_VOICE.ALERTS.GUIDE_SAVE_SUCCESS'));
    }
    return data;
  } catch {
    useAlert(t('BRAND_VOICE.ALERTS.GUIDE_SAVE_ERROR'));
    return null;
  } finally {
    isGuideSaving.value = false;
  }
};

const publicarGuia = async () => {
  isGuidePublishing.value = true;
  try {
    const draft = guideForm.id ? null : await salvarGuia({ silencioso: true });
    const versionId = guideForm.id || draft?.current_version?.id;
    if (!versionId) return;

    const { data } = await BrandVoiceAPI.publishGuide({ versionId });
    aplicarGuia(data);
    track(ACCOUNT_EVENTS.BRAND_VOICE_GUIDE_PUBLISHED, {
      versionId,
      version: guideForm.version,
      sectionCount: guideForm.sections.length,
    });
    useAlert(t('BRAND_VOICE.ALERTS.GUIDE_PUBLISH_SUCCESS'));
  } catch {
    useAlert(t('BRAND_VOICE.ALERTS.GUIDE_PUBLISH_ERROR'));
  } finally {
    isGuidePublishing.value = false;
  }
};

const selecionarVersaoGuia = async () => {
  isLoading.value = true;
  try {
    await carregarGuia(selectedGuideVersionId.value);
  } catch {
    useAlert(t('BRAND_VOICE.ALERTS.GUIDE_VERSION_LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const criarNovaVersao = () => {
  guideForm.id = null;
  guideForm.title = t('BRAND_VOICE.DEFAULTS.GUIDE_TITLE');
  guideForm.version = `v${guideVersions.value.length + 1}.0`;
  guideForm.status = 'draft';
  guideForm.summary = t('BRAND_VOICE.DEFAULTS.GUIDE_SUMMARY');
  guideForm.sections = normalizarSecoes(defaultGuideSections.value);
  selectedGuideVersionId.value = '';
};

const adicionarSecao = () => {
  const nextPosition = guideForm.sections.length + 1;
  guideForm.sections.push({
    id: null,
    slug: `secao-${nextPosition}`,
    title: t('BRAND_VOICE.DEFAULTS.SECTION_TITLE', { number: nextPosition }),
    position: nextPosition,
    body_text: '',
    metadata: {},
  });
};

const removerSecao = index => {
  guideForm.sections.splice(index, 1);
};

const moverSecao = (index, direction) => {
  const nextIndex = index + direction;
  if (nextIndex < 0 || nextIndex >= guideForm.sections.length) return;

  const [section] = guideForm.sections.splice(index, 1);
  guideForm.sections.splice(nextIndex, 0, section);
};

const restaurarPersonalidadePadrao = () => {
  form.brand_voice_personality_axes = normalizarEixos(
    defaultPersonalityAxes.value
  );
  form.brand_voice_voice_traits = normalizarTracos(defaultVoiceTraits.value);
};

const adicionarTracoVoz = () => {
  form.brand_voice_voice_traits.push({
    is: '',
    is_not: '',
  });
};

const removerTracoVoz = index => {
  form.brand_voice_voice_traits.splice(index, 1);
};

const brandVoiceDefaults = () => ({
  brand_voice_persona: t('BRAND_VOICE.DEFAULTS.PERSONA'),
  brand_voice_tone: t('BRAND_VOICE.DEFAULTS.TONE'),
  brand_voice_examples: t('BRAND_VOICE.DEFAULTS.EXAMPLES'),
  brand_voice_avoid_words: t('BRAND_VOICE.DEFAULTS.AVOID_WORDS'),
  brand_voice_greeting: t('BRAND_VOICE.DEFAULTS.GREETING', {
    namePlaceholder: '{{nome}}',
  }),
  brand_voice_signature: t('BRAND_VOICE.DEFAULTS.SIGNATURE'),
  brand_voice_enabled_for_agent: true,
});

const restaurarPadrao = () => {
  Object.assign(form, {
    ...brandVoiceDefaults(),
    brand_voice_personality_axes: normalizarEixos(defaultPersonalityAxes.value),
    brand_voice_voice_traits: normalizarTracos(defaultVoiceTraits.value),
  });
  showRestoreModal.value = false;
  track(ACCOUNT_EVENTS.BRAND_VOICE_DEFAULTS_RESTORED, {
    axisCount: form.brand_voice_personality_axes.length,
    traitCount: form.brand_voice_voice_traits.length,
  });
  useAlert(t('BRAND_VOICE.ALERTS.RESTORE_SUCCESS'));
};

const inputCls =
  'w-full px-3 py-2.5 text-[14px] text-lais-text bg-white border border-lais-border rounded-lais-sm focus:outline-none focus:border-lais-primary focus:ring-2 focus:ring-lais-primary-50 placeholder:text-lais-text-muted transition-all';

const smallButtonCls =
  'inline-flex items-center justify-center gap-1 rounded-lais-sm border border-lais-border px-2.5 py-1.5 text-[12px] font-semibold text-lais-text hover:border-lais-primary hover:text-lais-primary disabled:opacity-40 disabled:cursor-not-allowed';

const sectionCardTitle = (section, index) =>
  t('BRAND_VOICE.GUIDE.SECTION.CARD_TITLE', {
    number: index + 1,
    title: section.title || t('BRAND_VOICE.GUIDE.SECTION.FALLBACK_TITLE'),
  });

useEscClose(showRestoreModal, () => {
  showRestoreModal.value = false;
});

onMounted(carregar);
</script>

<template>
  <section
    class="flex flex-col bg-lais-bg-page font-redHatText"
    :class="props.embedded ? 'min-h-full' : 'h-full overflow-hidden'"
  >
    <LaisHeader
      :title="t('BRAND_VOICE.HEADER.TITLE')"
      :helper="t('BRAND_VOICE.HEADER.HELPER')"
      :breadcrumb="[
        {
          label: t('BRAND_VOICE.HEADER.BREADCRUMB_SETTINGS'),
          to: { name: 'general_settings_index' },
        },
        { label: t('BRAND_VOICE.HEADER.TITLE') },
      ]"
    >
      <template #actions>
        <LaisPrimaryButton
          variant="ghost"
          icon="i-lucide-rotate-ccw"
          :disabled="isLoading || isSaving"
          @click="showRestoreModal = true"
        >
          {{ t('BRAND_VOICE.ACTIONS.RESTORE_DEFAULTS') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton
          variant="primary"
          icon="i-lucide-check"
          :is-loading="isSaving"
          :disabled="isLoading"
          @click="salvar"
        >
          {{
            isSaving
              ? t('BRAND_VOICE.ACTIONS.SAVING')
              : t('BRAND_VOICE.ACTIONS.SAVE')
          }}
        </LaisPrimaryButton>
      </template>
    </LaisHeader>

    <div :class="props.embedded ? 'flex-1' : 'flex-1 overflow-auto'">
      <div class="w-full px-4 py-8 sm:px-6 lg:px-10">
        <div v-if="isLoading" class="text-center text-lais-text-muted py-20">
          {{ t('BRAND_VOICE.STATES.LOADING') }}
        </div>

        <div v-else class="flex flex-col gap-5">
          <div
            role="note"
            class="flex items-start gap-3 rounded-lais border border-lais-primary/20 bg-lais-primary-50 p-4 text-lais-text"
          >
            <span
              class="i-lucide-library-big mt-0.5 size-5 shrink-0 text-lais-primary"
              aria-hidden="true"
            />
            <div>
              <p class="text-[14px] font-semibold">
                {{ t('BRAND_VOICE.CAPTAIN_PUBLICATION.TITLE') }}
              </p>
              <p class="mt-1 text-[13px] leading-relaxed text-lais-text-muted">
                {{ t('BRAND_VOICE.CAPTAIN_PUBLICATION.DESCRIPTION') }}
              </p>
            </div>
          </div>

          <LaisCard :title="t('BRAND_VOICE.AGENT.TITLE')">
            <LaisToggle
              v-model="form.brand_voice_enabled_for_agent"
              :label="t('BRAND_VOICE.AGENT.LABEL')"
              :helper="t('BRAND_VOICE.AGENT.HELPER')"
            />
          </LaisCard>

          <LaisCard :title="t('BRAND_VOICE.PERSONA.TITLE')">
            <p class="text-[13px] text-lais-text-muted mb-3">
              {{ t('BRAND_VOICE.PERSONA.HELPER') }}
            </p>
            <LaisField :label="t('BRAND_VOICE.PERSONA.LABEL')">
              <textarea
                v-model="form.brand_voice_persona"
                rows="4"
                :class="inputCls"
                :placeholder="t('BRAND_VOICE.PERSONA.PLACEHOLDER')"
              />
            </LaisField>
          </LaisCard>

          <LaisCard :title="t('BRAND_VOICE.TONE.TITLE')">
            <LaisField :helper="t('BRAND_VOICE.TONE.HELPER')">
              <textarea
                v-model="form.brand_voice_tone"
                rows="3"
                :class="inputCls"
                :placeholder="t('BRAND_VOICE.TONE.PLACEHOLDER')"
              />
            </LaisField>
          </LaisCard>

          <LaisCard :title="t('BRAND_VOICE.AXES.TITLE')">
            <div class="flex justify-end mb-4">
              <button
                type="button"
                :class="smallButtonCls"
                @click="restaurarPersonalidadePadrao"
              >
                <span class="i-lucide-refresh-cw size-3.5" />
                {{ t('BRAND_VOICE.AXES.RESTORE') }}
              </button>
            </div>
            <div class="flex flex-col gap-5">
              <div
                v-for="(axis, index) in form.brand_voice_personality_axes"
                :key="`${axis.left}-${axis.right}-${index}`"
                class="rounded-lais-sm border border-lais-border bg-white p-4"
              >
                <div class="grid gap-3 md:grid-cols-[1fr_1fr_88px]">
                  <LaisField :label="t('BRAND_VOICE.AXES.LEFT_LABEL')">
                    <input
                      v-model="axis.left"
                      type="text"
                      :class="inputCls"
                      :placeholder="t('BRAND_VOICE.AXES.LEFT_PLACEHOLDER')"
                    />
                  </LaisField>
                  <LaisField :label="t('BRAND_VOICE.AXES.RIGHT_LABEL')">
                    <input
                      v-model="axis.right"
                      type="text"
                      :class="inputCls"
                      :placeholder="t('BRAND_VOICE.AXES.RIGHT_PLACEHOLDER')"
                    />
                  </LaisField>
                  <LaisField :label="t('BRAND_VOICE.AXES.VALUE_LABEL')">
                    <input
                      v-model.number="axis.value"
                      type="number"
                      min="0"
                      max="100"
                      :class="inputCls"
                    />
                  </LaisField>
                </div>
                <div class="mt-3">
                  <div
                    class="flex items-center justify-between text-[12px] text-lais-text-muted"
                  >
                    <span>{{ axis.left }}</span>
                    <span>
                      {{
                        t('BRAND_VOICE.AXES.VALUE_DISPLAY', {
                          value: axis.value,
                        })
                      }}
                    </span>
                    <span>{{ axis.right }}</span>
                  </div>
                  <input
                    v-model.number="axis.value"
                    type="range"
                    min="0"
                    max="100"
                    class="mt-2 h-2 w-full accent-lais-primary"
                  />
                </div>
                <LaisField
                  class="mt-3"
                  :label="t('BRAND_VOICE.AXES.NOTE_LABEL')"
                >
                  <input
                    v-model="axis.note"
                    type="text"
                    :class="inputCls"
                    :placeholder="t('BRAND_VOICE.AXES.NOTE_PLACEHOLDER')"
                  />
                </LaisField>
              </div>
            </div>
          </LaisCard>

          <LaisCard :title="t('BRAND_VOICE.TRAITS.TITLE')">
            <div class="flex justify-end mb-4">
              <button
                type="button"
                :class="smallButtonCls"
                @click="adicionarTracoVoz"
              >
                <span class="i-lucide-plus size-3.5" />
                {{ t('BRAND_VOICE.TRAITS.ADD') }}
              </button>
            </div>
            <div class="flex flex-col gap-3">
              <div
                v-for="(trait, index) in form.brand_voice_voice_traits"
                :key="`${trait.is}-${index}`"
                class="grid gap-3 rounded-lais-sm border border-lais-border bg-white p-4 md:grid-cols-[1fr_1fr_auto]"
              >
                <LaisField :label="t('BRAND_VOICE.TRAITS.IS_LABEL')">
                  <input
                    v-model="trait.is"
                    type="text"
                    :class="inputCls"
                    :placeholder="t('BRAND_VOICE.TRAITS.IS_PLACEHOLDER')"
                  />
                </LaisField>
                <LaisField :label="t('BRAND_VOICE.TRAITS.IS_NOT_LABEL')">
                  <input
                    v-model="trait.is_not"
                    type="text"
                    :class="inputCls"
                    :placeholder="t('BRAND_VOICE.TRAITS.IS_NOT_PLACEHOLDER')"
                  />
                </LaisField>
                <div class="flex items-end">
                  <button
                    type="button"
                    :class="smallButtonCls"
                    :disabled="form.brand_voice_voice_traits.length <= 1"
                    @click="removerTracoVoz(index)"
                  >
                    <span class="i-lucide-trash-2 size-3.5" />
                    {{ t('BRAND_VOICE.ACTIONS.REMOVE') }}
                  </button>
                </div>
              </div>
            </div>
          </LaisCard>

          <LaisCard :title="t('BRAND_VOICE.EXAMPLES.TITLE')">
            <p class="text-[13px] text-lais-text-muted mb-3">
              {{ t('BRAND_VOICE.EXAMPLES.HELPER') }}
            </p>
            <LaisField>
              <textarea
                v-model="form.brand_voice_examples"
                rows="7"
                class="font-mono text-[13px]"
                :class="[inputCls]"
                :placeholder="t('BRAND_VOICE.EXAMPLES.PLACEHOLDER')"
              />
            </LaisField>
          </LaisCard>

          <div class="grid gap-5 md:grid-cols-2">
            <LaisCard :title="t('BRAND_VOICE.GREETING.TITLE')">
              <LaisField :helper="t('BRAND_VOICE.GREETING.HELPER')">
                <input
                  v-model="form.brand_voice_greeting"
                  type="text"
                  :class="inputCls"
                  :placeholder="t('BRAND_VOICE.GREETING.PLACEHOLDER')"
                />
              </LaisField>
            </LaisCard>
            <LaisCard :title="t('BRAND_VOICE.SIGNATURE.TITLE')">
              <LaisField :helper="t('BRAND_VOICE.SIGNATURE.HELPER')">
                <input
                  v-model="form.brand_voice_signature"
                  type="text"
                  :class="inputCls"
                  :placeholder="t('BRAND_VOICE.SIGNATURE.PLACEHOLDER')"
                />
              </LaisField>
            </LaisCard>
          </div>

          <LaisCard :title="t('BRAND_VOICE.AVOID.TITLE')">
            <LaisField :helper="t('BRAND_VOICE.AVOID.HELPER')">
              <textarea
                v-model="form.brand_voice_avoid_words"
                rows="2"
                :class="inputCls"
                :placeholder="t('BRAND_VOICE.AVOID.PLACEHOLDER')"
              />
            </LaisField>
          </LaisCard>

          <div
            class="flex flex-col gap-3 border-t border-lais-border pt-6 md:flex-row md:items-center md:justify-between"
          >
            <div>
              <h2 class="text-[18px] font-semibold text-lais-text">
                {{ t('BRAND_VOICE.GUIDE.HEADER.TITLE') }}
              </h2>
              <p class="text-[13px] text-lais-text-muted">
                {{ t('BRAND_VOICE.GUIDE.HEADER.HELPER') }}
              </p>
              <p class="mt-1 text-[12px] font-medium text-lais-primary">
                {{ t('BRAND_VOICE.GUIDE.HEADER.CAPTAIN_NOTICE') }}
              </p>
            </div>
            <div class="flex flex-wrap items-center gap-2">
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-file-plus-2"
                :disabled="isGuideSaving || isGuidePublishing"
                @click="criarNovaVersao"
              >
                {{ t('BRAND_VOICE.GUIDE.ACTIONS.NEW_VERSION') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="ghost"
                icon="i-lucide-plus"
                :disabled="isGuideSaving || isGuidePublishing"
                @click="adicionarSecao"
              >
                {{ t('BRAND_VOICE.GUIDE.ACTIONS.NEW_SECTION') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="primary"
                icon="i-lucide-save"
                :is-loading="isGuideSaving"
                :disabled="isGuidePublishing"
                @click="salvarGuia"
              >
                {{ t('BRAND_VOICE.GUIDE.ACTIONS.SAVE') }}
              </LaisPrimaryButton>
              <LaisPrimaryButton
                variant="primary"
                icon="i-lucide-send"
                :is-loading="isGuidePublishing"
                :disabled="isGuideSaving"
                @click="publicarGuia"
              >
                {{ t('BRAND_VOICE.GUIDE.ACTIONS.PUBLISH') }}
              </LaisPrimaryButton>
            </div>
          </div>

          <LaisCard :title="t('BRAND_VOICE.GUIDE.VERSION.TITLE')">
            <div class="grid gap-4 lg:grid-cols-[1.1fr_1fr_0.7fr]">
              <LaisField :label="t('BRAND_VOICE.GUIDE.VERSION.EXISTING')">
                <select
                  v-model="selectedGuideVersionId"
                  :class="inputCls"
                  :disabled="guideVersionOptions.length === 0"
                  @change="selecionarVersaoGuia"
                >
                  <option value="">
                    {{ t('BRAND_VOICE.GUIDE.VERSION.NEW_DRAFT') }}
                  </option>
                  <option
                    v-for="option in guideVersionOptions"
                    :key="option.value"
                    :value="option.value"
                  >
                    {{ option.label }}
                  </option>
                </select>
              </LaisField>

              <LaisField :label="t('BRAND_VOICE.GUIDE.VERSION.TITLE_LABEL')">
                <input
                  v-model="guideForm.title"
                  type="text"
                  :class="inputCls"
                  :placeholder="
                    t('BRAND_VOICE.GUIDE.VERSION.TITLE_PLACEHOLDER')
                  "
                />
              </LaisField>

              <LaisField :label="t('BRAND_VOICE.GUIDE.VERSION.VERSION_LABEL')">
                <input
                  v-model="guideForm.version"
                  type="text"
                  :class="inputCls"
                  :placeholder="
                    t('BRAND_VOICE.GUIDE.VERSION.VERSION_PLACEHOLDER')
                  "
                />
              </LaisField>
            </div>

            <div class="mt-4 grid gap-4 lg:grid-cols-[1fr_180px]">
              <LaisField :label="t('BRAND_VOICE.GUIDE.VERSION.SUMMARY_LABEL')">
                <textarea
                  v-model="guideForm.summary"
                  rows="3"
                  :class="inputCls"
                  :placeholder="
                    t('BRAND_VOICE.GUIDE.VERSION.SUMMARY_PLACEHOLDER')
                  "
                />
              </LaisField>
              <div
                class="rounded-lais-sm border border-lais-border bg-lais-bg px-4 py-3"
              >
                <span
                  class="text-[11px] font-semibold uppercase text-lais-text-muted"
                >
                  {{ t('BRAND_VOICE.GUIDE.VERSION.STATUS_LABEL') }}
                </span>
                <div class="mt-2 text-[16px] font-semibold text-lais-text">
                  {{ statusLabel }}
                </div>
                <p class="mt-1 text-[12px] text-lais-text-muted">
                  {{
                    t('BRAND_VOICE.GUIDE.VERSION.SECTION_COUNT', {
                      count: guideForm.sections.length,
                    })
                  }}
                </p>
              </div>
            </div>
          </LaisCard>

          <LaisCard
            v-for="(section, index) in guideForm.sections"
            :key="`${section.slug}-${index}`"
            :title="sectionCardTitle(section, index)"
          >
            <div class="flex flex-wrap justify-end gap-2 mb-4">
              <button
                type="button"
                :class="smallButtonCls"
                :disabled="index === 0"
                @click="moverSecao(index, -1)"
              >
                <span class="i-lucide-arrow-up size-3.5" />
                {{ t('BRAND_VOICE.GUIDE.SECTION.MOVE_UP') }}
              </button>
              <button
                type="button"
                :class="smallButtonCls"
                :disabled="index === guideForm.sections.length - 1"
                @click="moverSecao(index, 1)"
              >
                <span class="i-lucide-arrow-down size-3.5" />
                {{ t('BRAND_VOICE.GUIDE.SECTION.MOVE_DOWN') }}
              </button>
              <button
                type="button"
                :class="smallButtonCls"
                :disabled="guideForm.sections.length <= 1"
                @click="removerSecao(index)"
              >
                <span class="i-lucide-trash-2 size-3.5" />
                {{ t('BRAND_VOICE.ACTIONS.REMOVE') }}
              </button>
            </div>

            <div class="grid gap-4 md:grid-cols-[1fr_0.7fr]">
              <LaisField :label="t('BRAND_VOICE.GUIDE.SECTION.TITLE_LABEL')">
                <input
                  v-model="section.title"
                  type="text"
                  :class="inputCls"
                  :placeholder="
                    t('BRAND_VOICE.GUIDE.SECTION.TITLE_PLACEHOLDER')
                  "
                />
              </LaisField>
              <LaisField :label="t('BRAND_VOICE.GUIDE.SECTION.SLUG_LABEL')">
                <input
                  v-model="section.slug"
                  type="text"
                  :class="inputCls"
                  :placeholder="t('BRAND_VOICE.GUIDE.SECTION.SLUG_PLACEHOLDER')"
                />
              </LaisField>
            </div>

            <LaisField
              class="mt-4"
              :label="t('BRAND_VOICE.GUIDE.SECTION.CONTENT_LABEL')"
              :helper="t('BRAND_VOICE.GUIDE.SECTION.CONTENT_HELPER')"
            >
              <textarea
                v-model="section.body_text"
                rows="5"
                :class="inputCls"
                :placeholder="
                  t('BRAND_VOICE.GUIDE.SECTION.CONTENT_PLACEHOLDER')
                "
              />
            </LaisField>
          </LaisCard>
        </div>
      </div>
    </div>

    <LaisConfirmModal
      :show="showRestoreModal"
      :title="t('BRAND_VOICE.MODAL.RESTORE.TITLE')"
      :description="t('BRAND_VOICE.MODAL.RESTORE.DESCRIPTION')"
      :confirm-text="t('BRAND_VOICE.MODAL.RESTORE.CONFIRM')"
      confirm-variant="primary"
      @close="showRestoreModal = false"
      @confirm="restaurarPadrao"
    />
  </section>
</template>
